/**
 * CareerIcons.ts
 * Handcrafted inline SVG iconography for:
 * - 3 Starter Universal Jobs (Dishwasher, Courier, Barista)
 * - 10 State Career Tracks across NY, CA, TX, FL, WA (Wall Street, Broadway, AI, GameDev, Energy, Pitmaster, Aerospace, Resort, Cloud, Roaster)
 */

export const CAREER_SVG_ICONS: Record<string, string> = {
  // --- Starter Universal Jobs ---

  /** Dishwasher: Sparkling porcelain plate, soapy bubbles, clean cutlery */
  job_starter_dishwasher: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="24" cy="24" rx="18" ry="18" fill="#1e293b" stroke="#38bdf8" stroke-width="2"/>
    <ellipse cx="24" cy="24" rx="13" ry="13" fill="#0f172a" stroke="#0284c7" stroke-width="1.2"/>
    <!-- Sparkling clean highlight -->
    <path d="M16 16 L 19 19 M 16 19 L 19 16" stroke="#f8fafc" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M30 14 L 32 16 M 30 16 L 32 14" stroke="#f8fafc" stroke-width="1.2" stroke-linecap="round"/>
    <!-- Soap bubbles -->
    <circle cx="34" cy="30" r="3" fill="rgba(56, 189, 248, 0.4)" stroke="#38bdf8" stroke-width="1"/>
    <circle cx="37" cy="25" r="1.8" fill="rgba(56, 189, 248, 0.5)" stroke="#38bdf8" stroke-width="0.8"/>
    <circle cx="28" cy="35" r="2.2" fill="rgba(56, 189, 248, 0.3)" stroke="#38bdf8" stroke-width="0.8"/>
  </svg>`,

  /** Delivery Courier: Fast delivery scooter with cargo box and wind lines */
  job_starter_courier: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Wheels -->
    <circle cx="14" cy="34" r="6" fill="#1e293b" stroke="#fbbf24" stroke-width="2"/>
    <circle cx="14" cy="34" r="2.5" fill="#f59e0b"/>
    <circle cx="34" cy="34" r="6" fill="#1e293b" stroke="#fbbf24" stroke-width="2"/>
    <circle cx="34" cy="34" r="2.5" fill="#f59e0b"/>
    <!-- Scooter Frame & Deck -->
    <path d="M14 34 L 22 34 L 28 22 L 34 34" stroke="#38bdf8" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
    <!-- Handlebars -->
    <path d="M28 22 L 26 14 L 30 13" stroke="#f8fafc" stroke-width="2" stroke-linecap="round"/>
    <!-- Delivery Box on Rear -->
    <rect x="8" y="16" width="10" height="11" rx="2" fill="#ef4444" stroke="#b91c1c" stroke-width="1.2"/>
    <path d="M8 20 L 18 20" stroke="#fca5a5" stroke-width="1"/>
    <!-- Motion Wind Streaks -->
    <line x1="2" y1="22" x2="6" y2="22" stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="4" y1="28" x2="9" y2="28" stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`,

  /** Barista: Steaming espresso portafilter and coffee cup */
  job_starter_barista: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Portafilter handle -->
    <path d="M8 36 L 18 28" stroke="#78350f" stroke-width="4" stroke-linecap="round"/>
    <circle cx="22" cy="25" r="7" fill="#475569" stroke="#94a3b8" stroke-width="1.5"/>
    <circle cx="22" cy="25" r="5" fill="#451a03"/>
    <!-- Coffee Cup & Crema -->
    <path d="M26 22 L 40 22 C 40 34, 26 34, 26 22 Z" fill="#0284c7" stroke="#38bdf8" stroke-width="1.5"/>
    <ellipse cx="33" cy="22" rx="7" ry="2.5" fill="#f59e0b"/>
    <!-- Steam -->
    <path d="M31 16 Q 29 12 32 8" stroke="rgba(255,255,255,0.6)" stroke-width="1.2" stroke-linecap="round" fill="none"/>
    <path d="M35 17 Q 37 13 34 9" stroke="rgba(255,255,255,0.6)" stroke-width="1.2" stroke-linecap="round" fill="none"/>
  </svg>`,

  // --- NY State Career Tracks ---

  /** NY: Wall Street Analyst: Candlestick stock chart, soaring bull trend arrow */
  ny_wall_street: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Grid -->
    <rect x="6" y="8" width="36" height="32" rx="4" fill="#0f172a" stroke="#334155" stroke-width="1.5"/>
    <!-- Candlesticks -->
    <line x1="13" y1="22" x2="13" y2="34" stroke="#ef4444" stroke-width="1"/>
    <rect x="11" y="25" width="4" height="6" fill="#ef4444" rx="0.5"/>
    <line x1="21" y1="18" x2="21" y2="32" stroke="#34d399" stroke-width="1"/>
    <rect x="19" y="20" width="4" height="8" fill="#34d399" rx="0.5"/>
    <line x1="29" y1="14" x2="29" y2="28" stroke="#34d399" stroke-width="1"/>
    <rect x="27" y="16" width="4" height="8" fill="#34d399" rx="0.5"/>
    <!-- Upward Trend Arrow -->
    <path d="M12 30 L 21 22 L 29 20 L 37 12" stroke="#fbbf24" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
    <polygon points="37,12 32,13 36,17" fill="#fbbf24"/>
  </svg>`,

  /** NY: Broadway Producer: Classic theater comedy and tragedy masks with stage curtain */
  ny_broadway: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Velvet Curtains -->
    <path d="M6 6 C 14 12, 10 38, 6 42 L 42 42 C 38 38, 34 12, 42 6 Z" fill="rgba(225, 29, 72, 0.2)" stroke="#e11d48" stroke-width="1.2"/>
    <!-- Comedy Mask (Left) -->
    <ellipse cx="20" cy="24" rx="8" ry="10" fill="#f8fafc" stroke="#f59e0b" stroke-width="1.5"/>
    <circle cx="17" cy="21" r="1.5" fill="#0f172a"/>
    <circle cx="23" cy="21" r="1.5" fill="#0f172a"/>
    <path d="M17 26 Q 20 30 23 26" stroke="#0f172a" stroke-width="1.5" stroke-linecap="round" fill="none"/>
    <!-- Tragedy Mask (Right) -->
    <ellipse cx="29" cy="28" rx="8" ry="10" fill="#cbd5e1" stroke="#38bdf8" stroke-width="1.5"/>
    <circle cx="26" cy="26" r="1.5" fill="#0f172a"/>
    <circle cx="32" cy="26" r="1.5" fill="#0f172a"/>
    <path d="M26 33 Q 29 29 32 33" stroke="#0f172a" stroke-width="1.5" stroke-linecap="round" fill="none"/>
  </svg>`,

  // --- CA State Career Tracks ---

  /** CA: Silicon Valley AI Engineer: Glowing microchip brain & neural node network */
  ca_ai_engineer: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- CPU Chip Core -->
    <rect x="14" y="14" width="20" height="20" rx="4" fill="#0f172a" stroke="#818cf8" stroke-width="2"/>
    <rect x="18" y="18" width="12" height="12" rx="2" fill="#312e81" stroke="#a5b4fc" stroke-width="1"/>
    <!-- Neural Node in center -->
    <circle cx="24" cy="24" r="3" fill="#38bdf8"/>
    <!-- Pins / Traces -->
    <line x1="20" y1="14" x2="20" y2="8" stroke="#818cf8" stroke-width="1.8"/>
    <line x1="28" y1="14" x2="28" y2="8" stroke="#818cf8" stroke-width="1.8"/>
    <line x1="20" y1="34" x2="20" y2="40" stroke="#818cf8" stroke-width="1.8"/>
    <line x1="28" y1="34" x2="28" y2="40" stroke="#818cf8" stroke-width="1.8"/>
    <line x1="14" y1="20" x2="8" y2="20" stroke="#818cf8" stroke-width="1.8"/>
    <line x1="14" y1="28" x2="8" y2="28" stroke="#818cf8" stroke-width="1.8"/>
    <line x1="34" y1="20" x2="40" y2="20" stroke="#818cf8" stroke-width="1.8"/>
    <line x1="34" y1="28" x2="40" y2="28" stroke="#818cf8" stroke-width="1.8"/>
    <!-- Glowing Node Connectors -->
    <circle cx="20" cy="8" r="1.5" fill="#34d399"/>
    <circle cx="28" cy="8" r="1.5" fill="#34d399"/>
    <circle cx="40" cy="20" r="1.5" fill="#34d399"/>
    <circle cx="40" cy="28" r="1.5" fill="#34d399"/>
  </svg>`,

  /** CA: Indie Game Developer: Retro pixel gamepad controller */
  ca_indie_gamedev: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Gamepad Body -->
    <path d="M12 18 C 8 18, 6 24, 8 32 C 10 38, 14 38, 18 34 L 22 30 L 26 30 L 30 34 C 34 38, 38 38, 40 32 C 42 24, 40 18, 36 18 Z" fill="#1e293b" stroke="#ec4899" stroke-width="2"/>
    <!-- D-Pad (Left) -->
    <rect x="13" y="24" width="6" height="2" fill="#f8fafc"/>
    <rect x="15" y="22" width="2" height="6" fill="#f8fafc"/>
    <!-- Action Buttons (Right) -->
    <circle cx="32" cy="23" r="1.5" fill="#f43f5e"/>
    <circle cx="36" cy="25" r="1.5" fill="#3b82f6"/>
    <circle cx="32" cy="27" r="1.5" fill="#eab308"/>
    <circle cx="28" cy="25" r="1.5" fill="#22c55e"/>
    <!-- Status Screen / Light in Center -->
    <rect x="22" y="23" width="4" height="2.5" rx="0.5" fill="#38bdf8"/>
  </svg>`,

  // --- TX State Career Tracks ---

  /** TX: Energy Tycoon: Power grid turbine & high-voltage lightning energy */
  tx_energy_tycoon: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Energy Pylon / Oil Derrick Base -->
    <polygon points="24,6 12,42 36,42" stroke="#eab308" stroke-width="2" fill="rgba(234, 179, 8, 0.15)"/>
    <line x1="16" y1="30" x2="32" y2="30" stroke="#fde047" stroke-width="1.8"/>
    <line x1="19" y1="18" x2="29" y2="18" stroke="#fde047" stroke-width="1.8"/>
    <line x1="16" y1="30" x2="29" y2="18" stroke="#fde047" stroke-width="1.2"/>
    <line x1="32" y1="30" x2="19" y2="18" stroke="#fde047" stroke-width="1.2"/>
    <!-- Electric Bolt Aura in Front -->
    <path d="M26 12 L 20 24 L 25 24 L 22 36 L 29 22 L 24 22 Z" fill="#facc15" stroke="#ca8a04" stroke-width="1"/>
  </svg>`,

  /** TX: Legendary Pitmaster: Crossed carving fork, chef knife, and flame */
  tx_pitmaster: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Roaring Flame -->
    <path d="M24 8 C 28 14, 34 18, 34 26 C 34 34, 28 40, 24 40 C 20 40, 14 34, 14 26 C 14 20, 20 16, 24 8 Z" fill="#ea580c"/>
    <path d="M24 16 C 26 20, 30 24, 30 28 C 30 33, 27 36, 24 36 C 21 36, 18 33, 18 28 C 18 23, 22 20, 24 16 Z" fill="#facc15"/>
    <!-- Crossed Meat Knife & Fork -->
    <line x1="10" y1="38" x2="38" y2="10" stroke="#f8fafc" stroke-width="2.5" stroke-linecap="round"/>
    <line x1="38" y1="38" x2="10" y2="10" stroke="#f8fafc" stroke-width="2.5" stroke-linecap="round"/>
    <!-- Wooden Handles -->
    <line x1="10" y1="38" x2="16" y2="32" stroke="#78350f" stroke-width="3" stroke-linecap="round"/>
    <line x1="38" y1="38" x2="32" y2="32" stroke="#78350f" stroke-width="3" stroke-linecap="round"/>
  </svg>`,

  // --- FL State Career Tracks ---

  /** FL: Aerospace Technician: Space rocket orbital launch with exhaust plume */
  fl_aerospace: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Exhaust Smoke / Plume -->
    <circle cx="16" cy="40" r="5" fill="#f97316"/>
    <circle cx="20" cy="38" r="4" fill="#fbbf24"/>
    <circle cx="14" cy="34" r="3" fill="#ef4444"/>
    <!-- Rocket Body tilted 45 deg -->
    <path d="M36 8 C 34 14, 28 20, 24 24 L 20 20 C 24 16, 30 10, 36 8 Z" fill="#f8fafc" stroke="#0284c7" stroke-width="1.5"/>
    <!-- Fins -->
    <polygon points="20,20 14,22 18,26" fill="#ef4444"/>
    <polygon points="24,24 22,30 26,28" fill="#ef4444"/>
    <!-- Cockpit Window -->
    <circle cx="28" cy="16" r="2.2" fill="#38bdf8" stroke="#0284c7" stroke-width="0.8"/>
  </svg>`,

  /** FL: Luxury Resort Manager: Palm tree & cabana sun umbrella */
  fl_resort_manager: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Sun -->
    <circle cx="34" cy="14" r="6" fill="#f59e0b"/>
    <!-- Island / Beach Dune -->
    <path d="M4 40 Q 24 34 44 40 Z" fill="#fde047"/>
    <!-- Palm Trunk -->
    <path d="M16 40 Q 18 28 14 18" stroke="#78350f" stroke-width="2.5" stroke-linecap="round" fill="none"/>
    <!-- Palm Fronds -->
    <path d="M14 18 Q 8 16 6 22 M 14 18 Q 14 10 10 12 M 14 18 Q 20 12 24 16 M 14 18 Q 22 22 20 26" stroke="#16a34a" stroke-width="2" stroke-linecap="round" fill="none"/>
    <!-- Striped Beach Umbrella -->
    <path d="M26 30 L 40 30 C 40 22, 26 22, 26 30 Z" fill="#ef4444"/>
    <path d="M30 30 C 30 24, 36 24, 36 30 Z" fill="#f8fafc"/>
    <line x1="33" y1="22" x2="33" y2="38" stroke="#475569" stroke-width="1.5"/>
  </svg>`,

  // --- WA State Career Tracks ---

  /** WA: Cloud Infrastructure Architect: Isometric high-tech cloud with server rack */
  wa_cloud_architect: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Cloud Outline -->
    <path d="M14 26 C 10 26, 8 22, 11 18 C 11 13, 16 11, 20 13 C 23 8, 31 8, 33 13 C 37 13, 40 16, 39 21 C 42 24, 40 28, 36 28 Z" fill="#0f172a" stroke="#0ea5e9" stroke-width="2"/>
    <!-- Server Blade Stack inside/under cloud -->
    <rect x="14" y="28" width="20" height="4" rx="1.5" fill="#1e293b" stroke="#38bdf8" stroke-width="1"/>
    <circle cx="17" cy="30" r="0.8" fill="#22c55e"/>
    <circle cx="20" cy="30" r="0.8" fill="#38bdf8"/>
    <rect x="14" y="34" width="20" height="4" rx="1.5" fill="#1e293b" stroke="#38bdf8" stroke-width="1"/>
    <circle cx="17" cy="36" r="0.8" fill="#22c55e"/>
    <circle cx="20" cy="36" r="0.8" fill="#38bdf8"/>
    <!-- Network Bus Lines -->
    <line x1="24" y1="24" x2="24" y2="28" stroke="#38bdf8" stroke-width="1.5"/>
    <line x1="24" y1="38" x2="24" y2="42" stroke="#38bdf8" stroke-width="1.5"/>
    <circle cx="24" cy="42" r="1.5" fill="#38bdf8"/>
  </svg>`,

  /** WA: Specialty Coffee Roaster: Vintage cylindrical roasting drum, flame, & beans */
  wa_specialty_roaster: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Roaster Drum -->
    <rect x="12" y="14" width="24" height="18" rx="4" fill="#334155" stroke="#f59e0b" stroke-width="2"/>
    <!-- Roasting Sight Glass & Thermometer -->
    <circle cx="24" cy="23" r="5" fill="#1e293b" stroke="#cbd5e1" stroke-width="1.2"/>
    <circle cx="24" cy="23" r="3" fill="#78350f"/>
    <!-- Bean Hopper at Top -->
    <polygon points="18,14 30,14 26,8 22,8" fill="#64748b" stroke="#94a3b8" stroke-width="1"/>
    <!-- Roasting Fire beneath -->
    <path d="M18 36 Q 21 32 24 35 Q 27 32 30 36 Z" fill="#ef4444"/>
    <path d="M21 36 Q 24 33 27 36 Z" fill="#facc15"/>
    <!-- Exhaust Chimney with Steam -->
    <rect x="30" y="8" width="4" height="6" fill="#475569"/>
    <path d="M32 6 Q 34 3 32 1" stroke="rgba(255,255,255,0.6)" stroke-width="1" stroke-linecap="round" fill="none"/>
  </svg>`,
};
